import { useRouter } from 'next/navigation';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { STREAMING_API_URL } from '@/app/app/research-assistant/constants';
import type {
  SessionSummary,
  SourceCounts,
  Message,
  SourceReference,
  ToolStatus,
  ContextUsage,
} from '@/app/app/research-assistant/types';
import { useAuth } from '@/app/auth-provider';
import { useProject } from '@/contexts/ProjectContext';
import { notifyResearchSessionListChanged } from '@/lib/research-assistant/session-list-events';
import { shouldClearSessionForProjectTransition } from '@/lib/research-assistant/session-project-transition';
import type { ResearchSessionScope } from '@/lib/research-assistant/session-scope';
import { finalizePendingToolStatus } from '@/lib/research-assistant/tool-status';
import { researchUiCacheKey, workspaceThreadHref } from '@/lib/slack-agent/session-ui';
import type { SlackResearchOrigin } from '@/lib/slack-agent/types';
import type {
  ResearchSessionMode,
  ResearchSessionSelectedPage,
  SessionAccessRole,
  SessionPayload,
  SessionVisibility,
} from '@/types/research-session';
import { createSessionLink, type VisibilityContext } from '@/utils/research-links';

// URL params for deep-linking
export interface ResearchSessionUrlParams {
  sessionId?: string | null;
  promptId?: string | null;
  jobId?: string | null;
  jobIds?: string[] | null;
  engine?: string | null;
  command?: string | null;
  question?: string | null;
  context?: string | null; // Base64-encoded VisibilityContext
  mode?: string | null; // Session mode (e.g., 'brand_perception' or 'onboarding')
  loadSessionList?: boolean; // Disable when another mounted surface owns the thread list.
}

/**
 * Aggregates sources from toolUsage into a single sources array.
 * This reconstructs the sources field from stored toolUsage data.
 */
function aggregateSourcesFromToolUsage(toolUsage?: ToolStatus[]): SourceReference[] | undefined {
  if (!toolUsage || toolUsage.length === 0) return undefined;

  const allSources: SourceReference[] = [];
  for (const tool of toolUsage) {
    if (tool.sources && tool.sources.length > 0) {
      allSources.push(...tool.sources);
    }
  }

  return allSources.length > 0 ? allSources : undefined;
}

function normalizeSourceCounts(sourceCounts?: SourceCounts | null): SourceCounts | null {
  if (!sourceCounts) {
    return null;
  }
  // totalTokens=-1 is a server-side sentinel for deferred hydration (counts are not ready yet).
  if (sourceCounts.totalTokens < 0) {
    return null;
  }
  return sourceCounts;
}

const EMPTY_SOURCE_COUNTS: SourceCounts = {
  answers: 0,
  citations: 0,
  pages: 0,
  total: 0,
  totalTokens: 0,
};

function getLatestMessagePreview(messages: Message[]): string | null {
  const latestMessage = [...messages]
    .reverse()
    .map(message => message.content.trim())
    .find(Boolean);

  return latestMessage ? latestMessage.slice(0, 160) : null;
}

function hydrateMessageSources(messages: Message[]): Message[] {
  return messages.map(message => {
    const toolUsage = message.toolUsage?.map(finalizePendingToolStatus);
    const traceTimeline = message.traceTimeline?.map(item =>
      item.type === 'tool' ? finalizePendingToolStatus(item) : item,
    );
    return {
      ...message,
      toolUsage,
      traceTimeline,
      sources: message.sources || aggregateSourcesFromToolUsage(toolUsage),
    };
  });
}

type CreateSessionResponse = {
  sessionId: string;
  jobId?: string | null;
  jobIds?: string[];
  promptId?: string | null;
  promptText?: string;
  engine?: string | null;
  sourceCounts?: SourceCounts | null;
  relevantPages?: SelectedPage[];
  createdAt: string;
  updatedAt: string;
  bootstrapSession?: SessionPayload;
  error?: {
    message?: string;
  };
};

type GetSessionResponse = {
  session: SessionPayload;
  error?: {
    message?: string;
    code?: string;
  };
};

interface SessionApiError extends Error {
  code?: string;
}

function createSessionApiError(message: string, code?: string): SessionApiError {
  const error = new Error(message) as SessionApiError;
  error.code = code;
  return error;
}

// Pre-selected page from Flash selection
export type SelectedPage = ResearchSessionSelectedPage;

interface SessionCreationOptions {
  promptId?: string;
  promptText?: string;
  projectId?: string | null;
  jobId?: string | null;
  jobIds?: string[];
  engine?: string;
  visibilityContext?: VisibilityContext;
  mode?: ResearchSessionMode;
  command?: string | null;
  question?: string | null;
  editor?: 'canvas' | 'website' | 'design-system';
  skipNavigation?: boolean;
  navigationMode?: 'push' | 'replace';
  deferHydration?: boolean;
  reuseEmptySession?: boolean;
  showReadyToast?: boolean;
}

interface LoadSessionByIdOptions {
  preferBootstrap?: boolean;
  redirectOnError?: boolean;
  backgroundRefresh?: boolean;
}

interface SessionListCacheEntry {
  sessions: SessionSummary[];
  hasMore: boolean;
}

interface SessionBootstrapCacheEntry {
  session: SessionPayload;
  cachedAt: number;
}

const SESSION_BOOTSTRAP_CACHE_TTL_MS = 2 * 60 * 1000;
const MAX_SESSION_BOOTSTRAP_CACHE_ENTRIES = 8;
const sessionListCache = new Map<string, SessionListCacheEntry>();
const sessionBootstrapCache = new Map<string, SessionBootstrapCacheEntry>();

function pruneSessionBootstrapCache(now = Date.now()): void {
  for (const [sessionId, entry] of sessionBootstrapCache) {
    if (now - entry.cachedAt > SESSION_BOOTSTRAP_CACHE_TTL_MS) {
      sessionBootstrapCache.delete(sessionId);
    }
  }

  if (sessionBootstrapCache.size <= MAX_SESSION_BOOTSTRAP_CACHE_ENTRIES) {
    return;
  }

  while (sessionBootstrapCache.size > MAX_SESSION_BOOTSTRAP_CACHE_ENTRIES) {
    const oldestSessionId = sessionBootstrapCache.keys().next().value;
    if (!oldestSessionId) return;
    sessionBootstrapCache.delete(oldestSessionId);
  }
}

function storeSessionBootstrap(actorId: string, session?: SessionPayload | null): void {
  if (!session?.id) return;
  const now = Date.now();
  pruneSessionBootstrapCache(now);
  const key = JSON.stringify([actorId, session.id]);
  sessionBootstrapCache.delete(key);
  sessionBootstrapCache.set(key, { session, cachedAt: now });
  pruneSessionBootstrapCache(now);
}

function readSessionBootstrap(actorId: string, sessionId: string): SessionPayload | null {
  const now = Date.now();
  pruneSessionBootstrapCache(now);
  const key = JSON.stringify([actorId, sessionId]);
  const cached = sessionBootstrapCache.get(key) || null;
  if (cached) {
    sessionBootstrapCache.delete(key);
    return cached.session;
  }
  return null;
}

function canSkipInitialGetSession(session: SessionPayload): boolean {
  return (session.messages || []).length === 0;
}

function readSessionListCache(projectId?: string | null): SessionListCacheEntry | null {
  if (!projectId) {
    return null;
  }
  return sessionListCache.get(projectId) || null;
}

function storeSessionListCache(
  projectId: string | null | undefined,
  sessions: SessionSummary[],
  hasMore: boolean,
): void {
  if (!projectId) {
    return;
  }
  sessionListCache.set(projectId, { sessions, hasMore });
}

function upsertSessionSummary(
  sessions: SessionSummary[],
  session: SessionSummary,
): SessionSummary[] {
  return [session, ...sessions.filter(item => item.id !== session.id)].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

interface UseResearchSessionReturn {
  // Session state
  sessionId: string | null;
  sessionProjectId: string | null;
  sessionScope: ResearchSessionScope | null;
  sessionOrigin: SlackResearchOrigin | null;
  slackDelivery: Record<string, unknown> | null;
  promptId: string | null; // For deep linking to citations/answers pages
  promptText: string;
  sessionName: string | null; // User-defined session name
  engine: string | null; // Session-level engine filter for display
  mode: string | null; // Legacy session mode metadata
  sourceCounts: SourceCounts | null;
  relevantPages: SelectedPage[]; // Pre-selected pages from Flash
  contextUsage: ContextUsage | null; // Token-based context window usage
  sessionCreatedAt: string | null;
  sessionUpdatedAt: string | null;
  sessionVisibility: SessionVisibility;
  accessRole: SessionAccessRole | null;
  ownerDisplayName: string | null;
  workspaceName: string | null;
  previousSessions: SessionSummary[];
  sortedSessions: SessionSummary[];
  sessionsLoading: boolean;

  // UI state
  isCreating: boolean;
  hasMoreSessions: boolean;
  loadingMore: boolean;
  isRenamingSession: boolean;
  isSuggestingName: boolean;

  // Actions
  resetSession: (options?: { skipNavigation?: boolean; preserveCurrentSession?: boolean }) => void;
  createSessionFromParams: (
    promptId: string,
    engine?: string,
    skipNavigation?: boolean
  ) => Promise<string | null>;
  bootstrapWorkspaceSession: (options?: {
    command?: string | null;
    question?: string | null;
    editor?: SessionCreationOptions['editor'];
    projectId?: string | null;
    visibilityContext?: VisibilityContext;
    skipNavigation?: boolean;
    mode?: ResearchSessionMode;
    promptText?: string;
  }) => Promise<string | null>;
  resumeSession: (session: SessionSummary) => Promise<void>;
  loadSessionById: (id: string, options?: LoadSessionByIdOptions) => Promise<boolean>;
  cancelSessionLoad: () => void;
  deleteSession: (id: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
  loadMoreSessions: () => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setSessionUpdatedAt: (date: string) => void;
  setContextUsage: (usage: ContextUsage | null) => void;
  setSessionName: React.Dispatch<React.SetStateAction<string | null>>;
  renameSession: (name: string | null) => Promise<boolean>;
  suggestSessionName: () => Promise<string | null>;
  updateSessionSharing: (visibility: SessionVisibility) => Promise<void>;
  forkCurrentSession: () => Promise<boolean>;

  // API helper for other hooks
  apiCall: (body: Record<string, unknown>, init?: RequestInit) => Promise<Response>;

  // Messages state (needed for session sync)
  messages: Message[];
}

export function useResearchSession(urlParams?: ResearchSessionUrlParams): UseResearchSessionReturn {
  const { user } = useAuth();
  const { currentProject, currentProjectId } = useProject();
  const router = useRouter();
  const projectId = currentProjectId || currentProject?.id || null;
  const cacheWorkspaceId = currentProject?.workspace_id || `legacy-project:${projectId}`;
  const getSessionListCache = useCallback((targetProjectId?: string | null) =>
    readSessionListCache(targetProjectId ? researchUiCacheKey(user?.uid, cacheWorkspaceId, targetProjectId) : null),
  [user?.uid, cacheWorkspaceId]);
  const setSessionListCache = useCallback((targetProjectId: string | null | undefined, sessions: SessionSummary[], hasMore: boolean) =>
    storeSessionListCache(targetProjectId ? researchUiCacheKey(user?.uid, cacheWorkspaceId, targetProjectId) : null, sessions, hasMore),
  [user?.uid, cacheWorkspaceId]);
  const cacheSessionBootstrap = useCallback((payload?: SessionPayload | null) => {
    if (user) storeSessionBootstrap(user.uid, payload);
  }, [user]);
  const consumeSessionBootstrap = useCallback((id: string) => user ? readSessionBootstrap(user.uid, id) : null, [user]);
  const sessionListEnabled = urlParams?.loadSessionList !== false;
  const cachedSessionList = sessionListEnabled ? getSessionListCache(projectId) : null;
  const activeProjectIdRef = useRef(projectId);
  const previousProjectIdRef = useRef(projectId);
  const sessionLoadRequestRef = useRef(0);
  const sessionLoadPendingRef = useRef<number | null>(null);
  const forkRequestRef = useRef<number | null>(null);
  const creatingSessionProjectIdRef = useRef<string | null>(null);
  const activeUserIdRef = useRef(user?.uid);
  const previousUserIdRef = useRef(user?.uid);
  activeUserIdRef.current = user?.uid;

  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const activeSessionIdRef = useRef(sessionId);
  activeSessionIdRef.current = sessionId;
  const [sessionProjectId, setSessionProjectId] = useState<string | null>(null);
  const [sessionScope, setSessionScope] = useState<ResearchSessionScope | null>(null);
  const [sessionOrigin, setSessionOrigin] = useState<SlackResearchOrigin | null>(null);
  const [slackDelivery, setSlackDelivery] = useState<Record<string, unknown> | null>(null);
  const [promptId, setPromptId] = useState<string | null>(null); // For deep linking
  const [promptText, setPromptText] = useState('');
  const [sessionName, setSessionName] = useState<string | null>(null); // User-defined name
  const [engine, setEngine] = useState<string | null>(null); // Session-level engine filter
  const [mode, setMode] = useState<string | null>(null); // Session mode for analytics
  const [sourceCounts, setSourceCounts] = useState<SourceCounts | null>(null);
  const [relevantPages, setRelevantPages] = useState<SelectedPage[]>([]); // Pre-selected pages
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null); // Token-based context tracking
  const [sessionCreatedAt, setSessionCreatedAt] = useState<string | null>(null);
  const [sessionUpdatedAt, setSessionUpdatedAt] = useState<string | null>(null);
  const [sessionVisibility, setSessionVisibility] = useState<SessionVisibility>('private');
  const [accessRole, setAccessRole] = useState<SessionAccessRole | null>(null);
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [previousSessions, setPreviousSessions] = useState<SessionSummary[]>(
    () => cachedSessionList?.sessions || [],
  );

  // UI state
  const [isCreating, setIsCreating] = useState(false);
  const isCreatingRef = useRef(false);
  const [sessionsLoading, setSessionsLoading] = useState(() => !cachedSessionList);
  const [hasMoreSessions, setHasMoreSessions] = useState(() => cachedSessionList?.hasMore || false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isRenamingSession, setIsRenamingSession] = useState(false);
  const [isSuggestingName, setIsSuggestingName] = useState(false);

  // API helper
  const apiCall = useCallback(
    async(body: Record<string, unknown>, init?: RequestInit) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const { headers: initHeaders, ...restInit } = init || {};
      const headers = new Headers(initHeaders);
      headers.set('Content-Type', 'application/json');
      headers.set('Authorization', `Bearer ${token}`);
      return fetch(STREAMING_API_URL, {
        ...restInit,
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
    },
    [user],
  );

  // Ref to track sessions for pagination without causing re-renders
  const previousSessionsRef = useRef<SessionSummary[]>([]);
  const hasMoreSessionsRef = useRef(hasMoreSessions);

  // Sync ref with state
  useEffect(() => {
    previousSessionsRef.current = previousSessions;
  }, [previousSessions]);

  useEffect(() => {
    hasMoreSessionsRef.current = hasMoreSessions;
  }, [hasMoreSessions]);

  useEffect(() => {
    activeProjectIdRef.current = projectId;
  }, [projectId]);

  useEffect(() => {
    if (!sessionListEnabled) {
      setPreviousSessions([]);
      setHasMoreSessions(false);
      setSessionsLoading(false);
      return;
    }
    const cached = getSessionListCache(projectId);
    if (cached) {
      setPreviousSessions(cached.sessions);
      setHasMoreSessions(cached.hasMore);
      setSessionsLoading(false);
      return;
    }

    setPreviousSessions([]);
    setHasMoreSessions(false);
    setSessionsLoading(Boolean(user && projectId));
  }, [getSessionListCache, projectId, sessionListEnabled, user]);

  // Load previous sessions
  const loadPreviousSessions = useCallback(
    async(append = false, showLoading = !append) => {
      if (!sessionListEnabled || !user || !projectId) return;
      const requestProjectId = projectId;
      if (append) {
        setLoadingMore(true);
      } else if (showLoading) {
        setSessionsLoading(true);
      }

      try {
        const body: Record<string, unknown> = {
          action: 'list_sessions',
          projectId: requestProjectId,
          limit: 20,
        };

        // If appending, use ref to get current sessions without dependency
        if (append && previousSessionsRef.current.length > 0) {
          const lastSession = previousSessionsRef.current[previousSessionsRef.current.length - 1];
          body.startAfter = lastSession.updatedAt;
        }

        const res = await apiCall(body);
        const data = await res.json();
        if (activeProjectIdRef.current !== requestProjectId || activeUserIdRef.current !== user.uid) return;

        const nextSessions = append
          ? [...previousSessionsRef.current, ...(data.sessions || [])]
          : data.sessions || [];
        const nextHasMore = data.hasMore || false;

        setPreviousSessions(nextSessions);
        setHasMoreSessions(nextHasMore);
        setSessionListCache(requestProjectId, nextSessions, nextHasMore);
        previousSessionsRef.current = nextSessions;
      } catch {
        if (activeProjectIdRef.current !== requestProjectId || activeUserIdRef.current !== user.uid) return;
        console.error('Failed to load sessions');
      } finally {
        if (activeProjectIdRef.current !== requestProjectId || activeUserIdRef.current !== user.uid) return;
        if (append) {
          setLoadingMore(false);
        } else {
          setSessionsLoading(false);
        }
      }
    },
    [user, projectId, apiCall, sessionListEnabled, setSessionListCache],
  );

  useEffect(() => {
    if (!sessionListEnabled || !user || !projectId) {
      return;
    }
    void loadPreviousSessions(false, !getSessionListCache(projectId));
  }, [getSessionListCache, loadPreviousSessions, projectId, sessionListEnabled, user]);

  const currentMessageCount = messages.length;
  const currentLatestMessagePreview = useMemo(() => getLatestMessagePreview(messages), [messages]);
  const currentSessionSnapshotRef = useRef<SessionSummary | null>(null);

  useEffect(() => {
    if (accessRole !== 'owner' || sessionScope?.kind === 'workspace') {
      currentSessionSnapshotRef.current = null;
      return;
    }
    if (sessionProjectId && projectId && sessionProjectId !== projectId) {
      currentSessionSnapshotRef.current = null;
      return;
    }
    if (!sessionId || !sessionCreatedAt || !sessionUpdatedAt) {
      currentSessionSnapshotRef.current = null;
      return;
    }

    const currentSessionSnapshot = {
      id: sessionId,
      projectId: sessionProjectId || projectId || null,
      promptText,
      name: sessionName,
      engine,
      sourceCounts: sourceCounts || EMPTY_SOURCE_COUNTS,
      messageCount: currentMessageCount,
      latestMessagePreview: currentLatestMessagePreview,
      createdAt: sessionCreatedAt,
      updatedAt: sessionUpdatedAt,
      visibility: sessionVisibility,
      accessRole: 'owner' as const,
      ownerDisplayName,
      workspaceName,
    };
    currentSessionSnapshotRef.current = currentSessionSnapshot;

    const cacheProjectId = sessionProjectId || projectId;
    if (!cacheProjectId) {
      return;
    }

    setPreviousSessions(prev => {
      const nextSessions = upsertSessionSummary(prev, currentSessionSnapshot);
      setSessionListCache(cacheProjectId, nextSessions, hasMoreSessionsRef.current);
      notifyResearchSessionListChanged({
        type: 'upsert',
        projectId: cacheProjectId,
        session: currentSessionSnapshot,
        hasMore: hasMoreSessionsRef.current,
      });
      previousSessionsRef.current = nextSessions;
      return nextSessions;
    });
  }, [
    sessionId,
    sessionProjectId,
    projectId,
    sessionCreatedAt,
    sessionUpdatedAt,
    promptText,
    sessionName,
    engine,
    sourceCounts,
    currentMessageCount,
    currentLatestMessagePreview,
    accessRole,
    sessionVisibility,
    ownerDisplayName,
    workspaceName,
    sessionScope,
    setSessionListCache,
  ]);

  const persistCurrentSessionSnapshot = useCallback(() => {
    const currentSessionSnapshot = currentSessionSnapshotRef.current;
    if (!currentSessionSnapshot) {
      return;
    }

    setPreviousSessions(prev => {
      const nextSessions = upsertSessionSummary(prev, currentSessionSnapshot);
      setSessionListCache(projectId, nextSessions, hasMoreSessionsRef.current);
      notifyResearchSessionListChanged({
        type: 'upsert',
        projectId: projectId || null,
        session: currentSessionSnapshot,
        hasMore: hasMoreSessionsRef.current,
      });
      previousSessionsRef.current = nextSessions;
      return nextSessions;
    });
  }, [projectId, setSessionListCache]);

  // Combine and sort all sessions
  const sortedSessions = useMemo(() => {
    const activeSessionMatchesProject =
      !sessionProjectId || !projectId || sessionProjectId === projectId;
    const allSessions: SessionSummary[] = [
      ...(accessRole === 'owner' && sessionScope?.kind !== 'workspace' &&
      activeSessionMatchesProject &&
      sessionId &&
      sessionCreatedAt &&
      sessionUpdatedAt
        ? [
          {
            id: sessionId,
            projectId: sessionProjectId || projectId || null,
            promptText,
            name: sessionName,
            engine,
            sourceCounts: sourceCounts || EMPTY_SOURCE_COUNTS,
            messageCount: currentMessageCount,
            latestMessagePreview: currentLatestMessagePreview,
            createdAt: sessionCreatedAt,
            updatedAt: sessionUpdatedAt,
            visibility: sessionVisibility,
            accessRole: 'owner' as const,
            ownerDisplayName,
            workspaceName,
            isCurrent: true,
          },
        ]
        : []),
      ...previousSessions.filter(
        session =>
          session.id !== sessionId &&
          session.scope?.kind !== 'workspace' &&
          (!session.projectId || !projectId || session.projectId === projectId),
      ),
    ];

    return allSessions.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [
    sessionId,
    promptText,
    sessionName,
    engine,
    sourceCounts,
    currentMessageCount,
    currentLatestMessagePreview,
    previousSessions,
    sessionCreatedAt,
    sessionUpdatedAt,
    sessionProjectId,
    projectId,
    accessRole,
    sessionVisibility,
    ownerDisplayName,
    workspaceName,
    sessionScope,
  ]);

  // Session management
  const clearActiveSessionState = useCallback(() => {
    sessionLoadRequestRef.current += 1;
    setSessionId(null);
    setSessionProjectId(null);
    setSessionScope(null);
    setSessionOrigin(null);
    setSlackDelivery(null);
    setPromptId(null);
    setPromptText('');
    setSessionName(null);
    setEngine(null);
    setMode(null);
    setSourceCounts(null);
    setRelevantPages([]);
    setContextUsage(null);
    setSessionCreatedAt(null);
    setSessionUpdatedAt(null);
    setSessionVisibility('private');
    setAccessRole(null);
    setOwnerDisplayName(null);
    setWorkspaceName(null);
    setMessages([]);
  }, []);

  useEffect(() => {
    if (previousUserIdRef.current !== user?.uid) {
      previousUserIdRef.current = user?.uid;
      currentSessionSnapshotRef.current = null;
      clearActiveSessionState();
    }
  }, [user?.uid, clearActiveSessionState]);

  const resetSession = useCallback(
    (options?: { skipNavigation?: boolean; preserveCurrentSession?: boolean }) => {
      if (options?.preserveCurrentSession !== false) {
        persistCurrentSessionSnapshot();
      }
      clearActiveSessionState();
      if (!options?.skipNavigation) {
        router.push('/app/workspace');
      }
    },
    [clearActiveSessionState, persistCurrentSessionSnapshot, router],
  );

  const cancelSessionLoad = useCallback(() => {
    sessionLoadRequestRef.current += 1;
  }, []);

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    if (previousProjectId === projectId) {
      return;
    }

    previousProjectIdRef.current = projectId;
    if (sessionScope?.kind === 'workspace' && sessionScope.workspaceId === currentProject?.workspace_id) return;
    if (
      shouldClearSessionForProjectTransition({
        previousProjectId,
        nextProjectId: projectId,
        sessionId,
        sessionProjectId,
        creatingSessionProjectId: creatingSessionProjectIdRef.current,
      })
    ) {
      currentSessionSnapshotRef.current = null;
      clearActiveSessionState();
    }
  }, [clearActiveSessionState, projectId, sessionId, sessionProjectId, sessionScope, currentProject?.workspace_id]);

  const resolveProjectIdForSession = useCallback((): string | undefined => {
    return projectId || undefined;
  }, [projectId]);

  const applySessionPayload = useCallback((session: SessionPayload) => {
    setSessionId(session.id);
    setSessionProjectId(session.projectId || null);
    setSessionScope(session.scope || null);
    setSessionOrigin(session.origin || null);
    setSlackDelivery(session.slackDelivery || null);
    setPromptId(session.promptId);
    setPromptText(session.promptText);
    setSessionName(session.name || null);
    setEngine(session.engine || null);
    setMode(session.mode || null);
    setSourceCounts(normalizeSourceCounts(session.sourceCounts || null));
    setRelevantPages(session.relevantPages || []);
    setContextUsage(session.contextUsage || null);
    setSessionCreatedAt(session.createdAt);
    setSessionUpdatedAt(session.updatedAt);
    setSessionVisibility(session.visibility || 'private');
    setAccessRole(session.accessRole || 'owner');
    setOwnerDisplayName(session.ownerDisplayName || null);
    setWorkspaceName(session.workspaceName || null);
  }, []);

  const createSessionInternal = useCallback(
    async(options: SessionCreationOptions): Promise<string | null> => {
      if (!user) {
        return null;
      }
      if (isCreatingRef.current) {
        return null;
      }
      persistCurrentSessionSnapshot();
      isCreatingRef.current = true;
      const requestId = (sessionLoadRequestRef.current += 1);

      const explicitProjectId =
        typeof options.projectId === 'string' && options.projectId.trim().length > 0
          ? options.projectId.trim()
          : undefined;
      const projectIdForSession =
        explicitProjectId ||
        (options.mode === 'onboarding' ? undefined : resolveProjectIdForSession());
      const promptIdToUse = options.promptId || '__workspace_default__';
      creatingSessionProjectIdRef.current = projectIdForSession || null;

      setIsCreating(true);
      try {
        const res = await apiCall({
          action: 'create_session',
          promptId: promptIdToUse,
          promptText: options.promptText,
          jobId: options.jobId || undefined,
          jobIds: options.jobIds && options.jobIds.length > 0 ? options.jobIds : undefined,
          engine: options.engine || undefined,
          projectId: projectIdForSession,
          mode: options.mode || undefined,
          visibilityContext: options.visibilityContext,
          deferHydration: options.deferHydration,
          includeBootstrapSession: true,
          reuseEmptySession: options.reuseEmptySession === true ? true : undefined,
        });

        const data = (await res.json()) as CreateSessionResponse;
        if (!res.ok) {
          throw new Error(data.error?.message || 'Failed to create session');
        }
        if (sessionLoadRequestRef.current !== requestId || activeUserIdRef.current !== user.uid) {
          return null;
        }

        const bootstrapSession = data.bootstrapSession;
        if (bootstrapSession) {
          if (!options.skipNavigation) {
            cacheSessionBootstrap(bootstrapSession);
          }
          applySessionPayload(bootstrapSession);
          setMessages(hydrateMessageSources(bootstrapSession.messages || []));
        } else {
          setSessionId(data.sessionId);
          setSessionProjectId(projectIdForSession || null);
          setSessionScope(null);
          setSessionOrigin(null);
          setSlackDelivery(null);
          setPromptId(data.promptId || promptIdToUse);
          setPromptText(data.promptText || options.promptText || 'Research query');
          setSessionName(null);
          setEngine(data.engine || null);
          setMode(options.mode || null);
          setSourceCounts(normalizeSourceCounts(data.sourceCounts || null));
          setRelevantPages(data.relevantPages || []);
          setContextUsage(null);
          setSessionCreatedAt(data.createdAt);
          setSessionUpdatedAt(data.updatedAt);
          setSessionVisibility('private');
          setAccessRole('owner');
          setOwnerDisplayName(null);
          setWorkspaceName(null);
          setMessages([]);
        }

        const effectiveSourceCounts = normalizeSourceCounts(
          bootstrapSession?.sourceCounts || data.sourceCounts || null,
        );
        const effectiveRelevantPages = bootstrapSession?.relevantPages || data.relevantPages || [];

        void loadPreviousSessions(false, false);

        if (options.showReadyToast !== false) {
          const answerCount = effectiveSourceCounts?.answers || 0;
          const citationCount = effectiveSourceCounts?.citations || 0;
          const pageCount = effectiveRelevantPages.length;
          toast.success(
            `Ready! ${answerCount} answers, ${citationCount} citations, ${pageCount} pages`,
          );
        }

        if (!options.skipNavigation) {
          const query = new URLSearchParams();
          if (options.command) {
            query.set('command', options.command);
          } else if (options.question) {
            query.set('question', options.question);
          }
          if (options.editor) query.set('editor', options.editor);
          const suffix = query.toString();
          const target = createSessionLink(data.sessionId) + (suffix ? `&${suffix}` : '');
          if (options.navigationMode === 'replace') {
            router.replace(target);
          } else {
            router.push(target);
          }
        }

        return data.sessionId;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create session');
        return null;
      } finally {
        if (creatingSessionProjectIdRef.current === (projectIdForSession || null)) {
          creatingSessionProjectIdRef.current = null;
        }
        setIsCreating(false);
        isCreatingRef.current = false;
      }
    },
    [
      apiCall,
      applySessionPayload,
      cacheSessionBootstrap,
      loadPreviousSessions,
      persistCurrentSessionSnapshot,
      resolveProjectIdForSession,
      router,
      user,
    ],
  );

  const createSessionFromParams = useCallback(
    async(promptId: string, engine?: string, skipNavigation = false): Promise<string | null> => {
      return createSessionInternal({
        promptId,
        engine: engine || undefined,
        skipNavigation,
        deferHydration: true,
        showReadyToast: false,
      });
    },
    [createSessionInternal],
  );

  const bootstrapWorkspaceSession = useCallback(
    async(options?: {
      command?: string | null;
      question?: string | null;
      editor?: SessionCreationOptions['editor'];
      projectId?: string | null;
      visibilityContext?: VisibilityContext;
      skipNavigation?: boolean;
      mode?: ResearchSessionMode;
      promptText?: string;
    }): Promise<string | null> => {
      return createSessionInternal({
        promptId: '__workspace_default__',
        promptText: options?.promptText || 'New research session',
        projectId: options?.projectId,
        visibilityContext: options?.visibilityContext,
        mode: options?.mode,
        command: options?.command,
        question: options?.question,
        editor: options?.editor,
        skipNavigation: options?.skipNavigation,
        deferHydration: true,
        navigationMode: 'replace',
        showReadyToast: false,
      });
    },
    [createSessionInternal],
  );

  const resumeSession = useCallback(
    async(session: SessionSummary) => {
      const requestId = (sessionLoadRequestRef.current += 1);
      try {
        persistCurrentSessionSnapshot();

        const res = await apiCall({ action: 'get_session', sessionId: session.id });
        const data = (await res.json()) as GetSessionResponse;
        if (sessionLoadRequestRef.current !== requestId || activeUserIdRef.current !== user?.uid) return;
        if (!res.ok) throw new Error(data.error?.message || 'Failed');

        applySessionPayload(data.session);
        setMessages(hydrateMessageSources(data.session.messages || []));

        // Navigate to session URL
        router.push(data.session.scope?.kind === 'workspace'
          ? workspaceThreadHref(session.id, data.session.scope.workspaceId) : createSessionLink(session.id));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed');
      }
    },
    [apiCall, applySessionPayload, persistCurrentSessionSnapshot, router, user?.uid],
  );

  // Load session by ID (for dynamic route)
  const loadSessionById = useCallback(
    async(id: string, options: LoadSessionByIdOptions = {}) => {
      if (options.backgroundRefresh && (
        forkRequestRef.current !== null ||
        sessionLoadPendingRef.current === sessionLoadRequestRef.current
      )) return false;
      const sessionLoadStartedAtMs = Date.now();
      const preferBootstrap = options.preferBootstrap === true;
      const shouldRedirectOnError = options.redirectOnError !== false;
      const projectIdAtLoadStart = activeProjectIdRef.current;
      const sessionIdAtLoadStart = sessionId;
      const requestId = (sessionLoadRequestRef.current += 1);
      const isCurrentLoad = () =>
        sessionLoadRequestRef.current === requestId &&
        activeUserIdRef.current === user?.uid &&
        activeProjectIdRef.current === projectIdAtLoadStart;
      persistCurrentSessionSnapshot();

      if (preferBootstrap) {
        const bootstrapSession = consumeSessionBootstrap(id);
        if (bootstrapSession) {
          if (!isCurrentLoad()) {
            return false;
          }
          applySessionPayload(bootstrapSession);
          setMessages(hydrateMessageSources(bootstrapSession.messages || []));
          if (canSkipInitialGetSession(bootstrapSession)) {
            return true;
          }
        }
      }
      try {
        sessionLoadPendingRef.current = requestId;
        const res = await apiCall({ action: 'get_session', sessionId: id });
        const data = (await res.json()) as GetSessionResponse;
        if (!res.ok) {
          throw createSessionApiError(data.error?.message || 'Failed', data.error?.code);
        }

        if (!isCurrentLoad()) {
          return false;
        }

        applySessionPayload(data.session);
        const serverMessages = hydrateMessageSources(data.session.messages || []);

        // Preserve locally added messages that were created while this load was in-flight.
        // This prevents races where a user turn is optimistically added during session load
        // but is not yet reflected in the server snapshot.
        setMessages(currentMessages => {
          if (currentMessages.length === 0 || sessionIdAtLoadStart !== id) return serverMessages;

          const serverMessageIds = new Set(serverMessages.map(m => m.id));
          const inFlightLocalMessages = currentMessages.filter(message => {
            if (serverMessageIds.has(message.id)) return false;
            const messageTimestampMs = Date.parse(message.timestamp || '');
            return (
              Number.isFinite(messageTimestampMs) && messageTimestampMs >= sessionLoadStartedAtMs
            );
          });

          if (inFlightLocalMessages.length === 0) return serverMessages;
          return [...serverMessages, ...inFlightLocalMessages];
        });
        return true;
      } catch (err) {
        if (!isCurrentLoad()) {
          return false;
        }
        if (shouldRedirectOnError) {
          toast.error(err instanceof Error ? err.message : 'Failed to load session');
          router.push('/app/workspace');
          return false;
        }
        throw err;
      } finally {
        if (sessionLoadPendingRef.current === requestId) sessionLoadPendingRef.current = null;
      }
    },
    [apiCall, applySessionPayload, consumeSessionBootstrap, persistCurrentSessionSnapshot, router, sessionId, user?.uid],
  );

  const deleteSession = useCallback(
    async(id: string) => {
      try {
        await apiCall({ action: 'delete_session', sessionId: id });
        setPreviousSessions(prev => {
          const nextSessions = prev.filter(s => s.id !== id);
          setSessionListCache(projectId, nextSessions, hasMoreSessionsRef.current);
          notifyResearchSessionListChanged({
            type: 'remove',
            projectId: projectId || null,
            sessionId: id,
            hasMore: hasMoreSessionsRef.current,
          });
          return nextSessions;
        });
        if (sessionId === id) {
          resetSession({ preserveCurrentSession: false });
        }
        toast.success('Session deleted');
      } catch {
        toast.error('Failed to delete');
      }
    },
    [apiCall, projectId, sessionId, resetSession, setSessionListCache],
  );

  // Rename session with a custom name
  const renameSession = useCallback(
    async(name: string | null) => {
      if (!sessionId) return false;
      setIsRenamingSession(true);
      try {
        const res = await apiCall({ action: 'rename_session', sessionId, name });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error?.message || 'Failed to rename');

        if (activeSessionIdRef.current === sessionId && activeProjectIdRef.current === projectId) {
          setSessionName(data.name);
        }
        // Update previous sessions list with new name
        setPreviousSessions(prev => {
          if (activeProjectIdRef.current !== projectId) return prev;
          const nextSessions = prev.map(s => (s.id === sessionId ? { ...s, name: data.name } : s));
          setSessionListCache(projectId, nextSessions, hasMoreSessionsRef.current);
          return nextSessions;
        });
        toast.success('Session renamed');
        return true;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to rename');
        return false;
      } finally {
        setIsRenamingSession(false);
      }
    },
    [apiCall, projectId, sessionId, setSessionListCache],
  );

  // Suggest a name using the server-side session title generator.
  const suggestSessionName = useCallback(async(): Promise<string | null> => {
    if (!sessionId) return null;
    setIsSuggestingName(true);
    try {
      const res = await apiCall({ action: 'suggest_session_name', sessionId });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Failed to suggest name');
      return data.suggestedName || null;
    } catch (err) {
      console.error('Failed to suggest name:', err);
      return null;
    } finally {
      setIsSuggestingName(false);
    }
  }, [apiCall, sessionId]);

  const updateSessionSharing = useCallback(
    async(visibility: SessionVisibility): Promise<void> => {
      if (!sessionId || accessRole !== 'owner') {
        throw new Error('Only the thread owner can change sharing');
      }
      const res = await apiCall({ action: 'update_session_sharing', sessionId, visibility });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Failed to update sharing');
      setSessionVisibility(data.sharing?.visibility || visibility);
      if (typeof data.sharing?.updatedAt === 'string') {
        setSessionUpdatedAt(data.sharing.updatedAt);
      }
    },
    [accessRole, apiCall, sessionId],
  );

  const forkCurrentSession = useCallback(async(): Promise<boolean> => {
    if (!sessionId || forkRequestRef.current !== null) return false;
    const requestId = (sessionLoadRequestRef.current += 1);
    forkRequestRef.current = requestId;
    try {
      const res = await apiCall({ action: 'fork_session', sessionId });
      const data = await res.json();
      if (sessionLoadRequestRef.current !== requestId || activeUserIdRef.current !== user?.uid) return false;
      if (!res.ok) throw new Error(data.error?.message || 'Failed to fork thread');
      const fork = data.bootstrapSession as SessionPayload | undefined;
      if (!fork?.id) throw new Error('Fork response was incomplete');

      persistCurrentSessionSnapshot();
      applySessionPayload(fork);
      setMessages(hydrateMessageSources(fork.messages || []));
      cacheSessionBootstrap(fork);
      router.push(fork.scope?.kind === 'workspace' ? workspaceThreadHref(fork.id, fork.scope.workspaceId) : createSessionLink(fork.id));
      return true;
    } finally {
      if (forkRequestRef.current === requestId) forkRequestRef.current = null;
    }
  }, [apiCall, applySessionPayload, cacheSessionBootstrap, persistCurrentSessionSnapshot, router, sessionId, user?.uid]);

  const loadMoreSessions = useCallback(() => {
    loadPreviousSessions(true);
  }, [loadPreviousSessions]);

  const refreshSessions = useCallback(() => {
    return loadPreviousSessions(false, false);
  }, [loadPreviousSessions]);

  // Auto-create session from URL params (deep-linking)
  const autoStartedRef = useRef(false);

  useEffect(() => {
    // Skip if already processed or no recognized deep-link parameters are present
    if (autoStartedRef.current) return;
    const hasSessionId = !!urlParams?.sessionId;
    const hasPromptId = !!urlParams?.promptId;
    const isLegacyBrandPerceptionMode = urlParams?.mode === 'brand_perception';
    const isOnboardingMode = urlParams?.mode === 'onboarding';
    const hasCommand = !!urlParams?.command;
    const hasQuestion = !!urlParams?.question;
    if (hasSessionId) return;
    if (
      !hasPromptId &&
      !isLegacyBrandPerceptionMode &&
      !isOnboardingMode &&
      !hasCommand &&
      !hasQuestion
    )
      return;
    const projectIdForSession = resolveProjectIdForSession();
    if (!user || (!projectIdForSession && !isOnboardingMode)) return;
    if (isCreating) return;

    autoStartedRef.current = true;

    // Parse context from URL if present
    let visibilityContext: VisibilityContext | undefined;
    if (urlParams.context) {
      try {
        visibilityContext = JSON.parse(atob(urlParams.context));
      } catch {
        console.warn('[Research] Failed to parse context from URL');
      }
    }

    void createSessionInternal({
      promptId: urlParams?.promptId || '__workspace_default__',
      jobId: urlParams?.jobId || null,
      jobIds: urlParams?.jobIds || undefined,
      engine: urlParams?.engine || undefined,
      visibilityContext,
      command: urlParams?.command || null,
      question: urlParams?.question || null,
      mode: isOnboardingMode
        ? 'onboarding'
        : isLegacyBrandPerceptionMode
          ? 'brand_perception'
          : undefined,
      deferHydration: true,
      navigationMode: 'replace',
      showReadyToast: false,
    }).then(createdSessionId => {
      if (!createdSessionId) {
        autoStartedRef.current = false;
      }
    });
  }, [
    createSessionInternal,
    isCreating,
    resolveProjectIdForSession,
    urlParams?.command,
    urlParams?.context,
    urlParams?.engine,
    urlParams?.jobId,
    urlParams?.jobIds,
    urlParams?.mode,
    urlParams?.promptId,
    urlParams?.question,
    urlParams?.sessionId,
    user,
  ]);

  const stateBelongsToUser = previousUserIdRef.current === user?.uid;
  return {
    // Session state
    sessionId: stateBelongsToUser ? sessionId : null,
    sessionProjectId: stateBelongsToUser ? sessionProjectId : null,
    sessionScope: stateBelongsToUser ? sessionScope : null,
    sessionOrigin: stateBelongsToUser ? sessionOrigin : null,
    slackDelivery: stateBelongsToUser ? slackDelivery : null,
    promptId: stateBelongsToUser ? promptId : null,
    promptText: stateBelongsToUser ? promptText : '',
    sessionName: stateBelongsToUser ? sessionName : null,
    engine,
    mode,
    sourceCounts: stateBelongsToUser ? sourceCounts : null,
    relevantPages: stateBelongsToUser ? relevantPages : [],
    contextUsage: stateBelongsToUser ? contextUsage : null,
    sessionCreatedAt,
    sessionUpdatedAt,
    sessionVisibility,
    accessRole,
    ownerDisplayName: stateBelongsToUser ? ownerDisplayName : null,
    workspaceName: stateBelongsToUser ? workspaceName : null,
    previousSessions: stateBelongsToUser ? previousSessions : [],
    sortedSessions: stateBelongsToUser ? sortedSessions : [],
    sessionsLoading,

    // UI state
    isCreating,
    hasMoreSessions,
    loadingMore,
    isRenamingSession,
    isSuggestingName,

    // Actions
    resetSession,
    createSessionFromParams,
    bootstrapWorkspaceSession,
    resumeSession,
    loadSessionById,
    cancelSessionLoad,
    deleteSession,
    refreshSessions,
    loadMoreSessions,
    setMessages,
    setSessionUpdatedAt,
    setContextUsage,
    setSessionName,
    renameSession,
    suggestSessionName,
    updateSessionSharing,
    forkCurrentSession,

    // API helper
    apiCall,

    // Messages
    messages: stateBelongsToUser ? messages : [],
  };
}
